/**
 * Phase 27.37.G — SMS Broadcasts Page.
 *
 * KR-admin only. Compose, preview audience, send now or schedule
 * for later, and review past broadcasts. Every send is delivered
 * through the Agent SMS channel with phone-tied consent enforced.
 *
 * Route: /sms-broadcasts
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Send, Clock, Users, MessageSquare, XCircle, RefreshCw, Play } from 'lucide-react';
import { smsBroadcastAPI, agencyAPI } from '@/lib/api';
import { getErrorMessage, formatPhone } from '@/lib/utils';

const ROLE_OPTIONS = [
  { value: 'Agent', label: 'Agentes' },
  { value: 'Agency_Principal', label: 'Principales de agencia' },
  { value: 'Agency_Staff', label: 'Staff de agencia' },
  { value: 'KR_AdminOps', label: 'KR Admin Ops' },
  { value: 'KR_SuperAdmin', label: 'KR SuperAdmin' },
];

const AGENT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'released', label: 'Released' },
  { value: 'inactive', label: 'Inactive' },
];

const statusColor = (status) => {
  switch (status) {
    case 'sent':      return 'bg-emerald-100 text-emerald-800';
    case 'sending':   return 'bg-blue-100 text-blue-800';
    case 'scheduled': return 'bg-amber-100 text-amber-800';
    case 'draft':     return 'bg-slate-100 text-slate-800';
    case 'cancelled': return 'bg-slate-100 text-slate-500';
    case 'failed':    return 'bg-rose-100 text-rose-800';
    default:          return 'bg-slate-100 text-slate-700';
  }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function SmsBroadcastsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCompose, setOpenCompose] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await smsBroadcastAPI.list();
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'No se pudo cargar la lista de broadcasts'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onCancel = async (b) => {
    if (!window.confirm(`Cancel broadcast "${b.name}"?`)) return;
    try {
      await smsBroadcastAPI.cancel(b.broadcast_id);
      toast.success('Broadcast cancelled');
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'No se pudo cancelar'));
    }
  };

  const onDispatchNow = async (b) => {
    if (!window.confirm(`Send broadcast "${b.name}" now?`)) return;
    try {
      const r = await smsBroadcastAPI.dispatchNow(b.broadcast_id);
      toast.success(`Enviado: ${r.data?.stats?.sent || 0}, saltados: ${r.data?.stats?.skipped || 0}, fallidos: ${r.data?.stats?.failed || 0}`);
      await load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'No se pudo enviar'));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="sms-broadcasts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6" /> SMS Broadcasts
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Send bulk SMS messages to opted-in agents. Immediate or scheduled delivery.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="broadcasts-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={() => setOpenCompose(true)} data-testid="broadcast-new-btn">
            <Send className="w-4 h-4 mr-2" /> New Broadcast
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Historial de Broadcasts</CardTitle></CardHeader>
        <CardContent>
          {rows.length === 0 ? (
            <div className="text-slate-500 text-sm py-8 text-center" data-testid="broadcasts-empty">
              No broadcasts yet. Create the first one with &ldquo;New Broadcast&rdquo;.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Nombre</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Estado</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Programado</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Stats</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-600">Creado</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => (
                    <tr key={b.broadcast_id} className="border-t hover:bg-slate-50">
                      <td className="py-2 px-3">
                        <button
                          className="text-left font-medium text-slate-900 hover:underline"
                          data-testid={`broadcast-open-${b.broadcast_id}`}
                          onClick={() => setSelected(b)}
                        >
                          {b.name}
                        </button>
                        <div className="text-xs text-slate-500 line-clamp-1 max-w-xs">{b.body}</div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={statusColor(b.status)}>{b.status}</Badge>
                      </td>
                      <td className="py-2 px-3 text-slate-600">
                        {b.scheduled_at ? fmtDate(b.scheduled_at) : (b.send_mode === 'immediate' ? 'Inmediato' : '—')}
                      </td>
                      <td className="py-2 px-3 text-slate-600">
                        {b.stats?.targeted ?? 0} obj · {b.stats?.sent ?? 0} env · {b.stats?.failed ?? 0} fall
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-xs">{fmtDate(b.created_at)}</td>
                      <td className="py-2 px-3 text-right space-x-2">
                        {['scheduled', 'draft'].includes(b.status) && (
                          <>
                            <Button size="sm" variant="outline"
                              data-testid={`broadcast-dispatch-${b.broadcast_id}`}
                              onClick={() => onDispatchNow(b)}>
                              <Play className="w-3 h-3 mr-1" /> Send now
                            </Button>
                            <Button size="sm" variant="ghost" className="text-rose-600"
                              data-testid={`broadcast-cancel-${b.broadcast_id}`}
                              onClick={() => onCancel(b)}>
                              <XCircle className="w-3 h-3 mr-1" /> Cancel
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <ComposeBroadcastDialog open={openCompose} onOpenChange={setOpenCompose} onDone={load} />
      <BroadcastDetailDialog broadcast={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function ComposeBroadcastDialog({ open, onOpenChange, onDone }) {
  const [name, setName] = useState('');
  const [body, setBody] = useState('');
  const [roles, setRoles] = useState([]);
  const [agencyIds, setAgencyIds] = useState([]);
  const [agentStatuses, setAgentStatuses] = useState([]);
  const [sendMode, setSendMode] = useState('immediate');
  const [scheduledAt, setScheduledAt] = useState('');
  const [preview, setPreview] = useState({ total: 0, sample: [] });
  const [agencies, setAgencies] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setBody(''); setRoles([]); setAgencyIds([]);
    setAgentStatuses([]); setSendMode('immediate'); setScheduledAt('');
    setPreview({ total: 0, sample: [] });
    agencyAPI.list().then((r) => setAgencies(r.data || [])).catch(() => setAgencies([]));
  }, [open]);

  const audience = { roles, agency_ids: agencyIds, agent_statuses: agentStatuses };

  const onPreview = async () => {
    try {
      const r = await smsBroadcastAPI.preview(audience);
      setPreview(r.data || { total: 0, sample: [] });
    } catch (e) {
      toast.error(getErrorMessage(e, 'No se pudo previsualizar la audiencia'));
    }
  };

  const toggle = (list, setter, value) => {
    if (list.includes(value)) setter(list.filter((v) => v !== value));
    else setter([...list, value]);
  };

  const canSubmit = () =>
    name.trim().length > 0 &&
    body.trim().length > 0 &&
    (sendMode !== 'scheduled' || !!scheduledAt);

  const submit = async () => {
    if (!canSubmit()) {
      toast.error('Completa nombre, mensaje y (si aplica) fecha programada');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        body: body.trim(),
        audience,
        send_mode: sendMode,
        scheduled_at: sendMode === 'scheduled'
          ? new Date(scheduledAt).toISOString()
          : null,
      };
      const r = await smsBroadcastAPI.create(payload);
      if (sendMode === 'immediate') {
        toast.success(`Broadcast enviado — ${r.data?.stats?.sent || 0} entregados`);
      } else if (sendMode === 'scheduled') {
        toast.success('Broadcast programado');
      } else {
        toast.success('Broadcast guardado como borrador');
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'No se pudo crear el broadcast'));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="broadcast-compose-dialog">
        <DialogHeader>
          <DialogTitle>New SMS Broadcast</DialogTitle>
          <DialogDescription>
            Se enviará por el canal Agent SMS. Solo agentes con opt-in activo recibirán el mensaje.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="bc-name">Nombre interno</Label>
            <Input id="bc-name" data-testid="bc-name-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Recordatorio evento 15-nov" maxLength={120} />
          </div>

          <div>
            <Label htmlFor="bc-body">Mensaje</Label>
            <Textarea id="bc-body" data-testid="bc-body-input" value={body} onChange={(e) => setBody(e.target.value)}
              rows={4} maxLength={1200}
              placeholder="Ej: KR Hub: Recordatorio del evento el viernes 15 a las 6 PM..." />
            <div className="text-xs text-slate-500 mt-1">{body.length} / 1200 caracteres</div>
          </div>

          <div className="border rounded-md p-3 space-y-3 bg-slate-50" data-testid="bc-audience-block">
            <div className="text-sm font-medium flex items-center gap-2">
              <Users className="w-4 h-4" /> Audiencia (filtros combinables)
            </div>

            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Roles</div>
              <div className="flex flex-wrap gap-3">
                {ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`bc-role-${r.value}`}
                      checked={roles.includes(r.value)}
                      onCheckedChange={() => toggle(roles, setRoles, r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium text-slate-700 mb-1">Estado del agente</div>
              <div className="flex flex-wrap gap-3">
                {AGENT_STATUS_OPTIONS.map((s) => (
                  <label key={s.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      data-testid={`bc-status-${s.value}`}
                      checked={agentStatuses.includes(s.value)}
                      onCheckedChange={() => toggle(agentStatuses, setAgentStatuses, s.value)}
                    />
                    {s.label}
                  </label>
                ))}
              </div>
            </div>

            {agencies.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-700 mb-1">Agencias (opcional)</div>
                <div className="max-h-32 overflow-y-auto border rounded bg-white p-2">
                  {agencies.map((a) => (
                    <label key={a.agency_id} className="flex items-center gap-2 text-sm py-0.5">
                      <Checkbox
                        data-testid={`bc-agency-${a.agency_id}`}
                        checked={agencyIds.includes(a.agency_id)}
                        onCheckedChange={() => toggle(agencyIds, setAgencyIds, a.agency_id)}
                      />
                      {a.agency_name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <Button size="sm" variant="outline" onClick={onPreview} data-testid="bc-preview-btn">
                Previsualizar audiencia
              </Button>
              <div className="text-sm text-slate-700" data-testid="bc-preview-count">
                <strong>{preview.total}</strong> agentes coinciden y tienen SMS activado
              </div>
            </div>
            {preview.sample?.length > 0 && (
              <div className="text-xs text-slate-500">
                Ej: {preview.sample.map((s) => s.name).join(', ')}{preview.total > preview.sample.length ? '…' : ''}
              </div>
            )}
          </div>

          <div className="border rounded-md p-3 space-y-3">
            <Label>Send mode</Label>
            <Select value={sendMode} onValueChange={setSendMode}>
              <SelectTrigger data-testid="bc-send-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="immediate">Send immediately</SelectItem>
                <SelectItem value="scheduled">Programado (fecha/hora futura)</SelectItem>
                <SelectItem value="draft">Save as draft</SelectItem>
              </SelectContent>
            </Select>

            {sendMode === 'scheduled' && (
              <div>
                <Label htmlFor="bc-scheduled">Fecha y hora (zona local)</Label>
                <Input id="bc-scheduled" data-testid="bc-scheduled-input" type="datetime-local"
                  value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> El scheduler despacha cada 5 minutos.
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bc-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={submitting || !canSubmit()} data-testid="bc-submit-btn">
            {submitting ? 'Sending…' :
              sendMode === 'immediate' ? 'Send now' :
              sendMode === 'scheduled' ? 'Schedule' : 'Save draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastDetailDialog({ broadcast, onClose }) {
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!broadcast) { setDetails(null); return; }
    smsBroadcastAPI.get(broadcast.broadcast_id)
      .then((r) => setDetails(r.data))
      .catch(() => setDetails(broadcast));
  }, [broadcast]);

  if (!broadcast) return null;
  const d = details || broadcast;
  const recipients = d.recipients || [];

  return (
    <Dialog open={!!broadcast} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="broadcast-detail-dialog">
        <DialogHeader>
          <DialogTitle>{d.name}</DialogTitle>
          <DialogDescription>{fmtDate(d.created_at)} · por {d.created_by_name || '—'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="p-3 bg-slate-50 rounded text-sm whitespace-pre-wrap">{d.body}</div>
          <div className="grid grid-cols-4 gap-3 text-sm">
            <div><div className="text-xs text-slate-500">Estado</div><Badge className={statusColor(d.status)}>{d.status}</Badge></div>
            <div><div className="text-xs text-slate-500">Objetivos</div><div className="font-semibold">{d.stats?.targeted ?? 0}</div></div>
            <div><div className="text-xs text-slate-500">Enviados</div><div className="font-semibold text-emerald-700">{d.stats?.sent ?? 0}</div></div>
            <div><div className="text-xs text-slate-500">Fallidos</div><div className="font-semibold text-rose-700">{d.stats?.failed ?? 0}</div></div>
          </div>
          {recipients.length > 0 && (
            <div>
              <div className="text-sm font-medium mb-2">Destinatarios</div>
              <div className="max-h-72 overflow-y-auto border rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-3">Agente</th>
                      <th className="text-left py-2 px-3">Phone</th>
                      <th className="text-left py-2 px-3">Estado</th>
                      <th className="text-left py-2 px-3">Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.user_id} className="border-t">
                        <td className="py-1.5 px-3">{r.name || r.user_id}</td>
                        <td className="py-1.5 px-3 text-slate-600">{formatPhone(r.phone) || '—'}</td>
                        <td className="py-1.5 px-3">
                          <Badge className={statusColor(r.status === 'sent' ? 'sent' : (r.status === 'failed' ? 'failed' : 'draft'))}>{r.status}</Badge>
                        </td>
                        <td className="py-1.5 px-3 text-slate-500">{r.reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
